'use client';
import { useSearchParams } from 'next/navigation';
import React, {
  ReactNode,
  Suspense,
  useEffect,
  useState,
  useRef,
  useCallback,
} from 'react';
import { regionedAppHostUrl, getCookie } from './utils';
import { NavContextType, UserResponse } from './type';
import { beehiveFetcher, beehiveGetFetcher } from '@/services/beehive';
import useSWRMutation from 'swr/mutation';
import { clearSiteDetailFromStorage } from '@context/explorer';
import { explorerAnalytics } from '@/lib/analytics';
import { pendoTrack } from '@/lib/pendo/trackPendoEvent';
import { setSentryUser } from '@/lib/sentry';

export const ClientAuthContext = React.createContext<NavContextType>({});

// 5 minutes polling interval for token refresh
const POLLING_INTERVAL = 5 * 60 * 1000;

function ClientAuthProviderSuspense({
  children,
  authCode,
}: {
  children: ReactNode;
  authCode: string | null;
}) {
  const [user, setUser] = useState<UserResponse | null>(null);
  const [loading, setLoading] = useState(true); // TRUE by default
  const initialCheckDoneRef = useRef(false);

  // Login mutation for auth code exchange
  const { trigger: sendLoginRequest } = useSWRMutation(
    'public/api/help-docs/login',
    beehiveFetcher,
    {
      onSuccess: (data) => {
        if (data?.email) {
          setUser(data);
          explorerAnalytics.loginCompleted({
            siteName: data.active_site?.site_name || '',
            loginMethod: 'manual',
          });
          pendoTrack('auth_action', {
            action: 'completed',
            source: 'manual',
          });
          setSentryUser({
            email: data.email,
            username: data.username,
          });
        } else {
          setUser(null);
          setSentryUser(null);
          clearSiteDetailFromStorage();
        }
        setLoading(false); // Always set to false on success
      },
      onError: () => {
        setLoading(false); // Always set to false on error
        setUser(null);
        setSentryUser(null);
        clearSiteDetailFromStorage();
      },
    }
  );

  // Fetch current user from /users/me endpoint
  const fetchUserData = useCallback(
    async (options?: {
      silent?: boolean;
      isInitialLoad?: boolean;
      checkAutoLogoutOnly?: boolean;
    }) => {
      // Only show loading for non-silent calls
      if (!options?.silent) {
        setLoading(true);
      }

      // Determine cookie check mode:
      // - isInitialLoad: FULL check (AUTO_LOGIN + AUTO_LOGOUT)
      // - checkAutoLogoutOnly: PARTIAL check (AUTO_LOGOUT only)
      // - neither: NO cookie check (polling)
      const shouldCheckCookie =
        options?.isInitialLoad || options?.checkAutoLogoutOnly;
      const hasServerTimestamp = shouldCheckCookie
        ? !!getCookie('server_timestamp')
        : null;

      try {
        const userData = await beehiveGetFetcher(
          'public/api/help-docs/users/me'
        );
        if (userData?.email) {
          // /users/me SUCCESS

          // AUTO_LOGOUT check - on initial load OR visibility change
          if (shouldCheckCookie && !hasServerTimestamp) {
            // User has API Explorer session but CB app cookie is gone
            await beehiveGetFetcher('public/api/help-docs/logout');
            setUser(null);
            setSentryUser(null);
            clearSiteDetailFromStorage();

            // Dispatch event to close any open dialog (cross-tab scenario)
            // This handles the case where user completed logout in another tab
            window.dispatchEvent(new CustomEvent('authStateResolved'));
          } else {
            // Normal logged-in state
            setUser(userData);
            setSentryUser({
              email: userData.email,
              username: userData.username,
            });

            // Dispatch event to close any open dialog (cross-tab scenario)
            // This handles the case where user completed login in another tab
            if (shouldCheckCookie) {
              window.dispatchEvent(new CustomEvent('authStateResolved'));
            }
          }
        } else {
          setUser(null);
          setSentryUser(null);
          clearSiteDetailFromStorage();
        }
      } catch {
        // /users/me FAILURE
        setUser(null);
        setSentryUser(null);
        clearSiteDetailFromStorage();

        // AUTO_LOGIN check - ONLY on initial load (NOT on visibility change)
        if (options?.isInitialLoad && hasServerTimestamp) {
          // CB app cookie present but no API Explorer session
          // Dispatch custom event for UnsavedChangesProvider to handle
          // This allows showing the custom dialog instead of native beforeunload
          setLoading(false);
          explorerAnalytics.autoLoginTriggered({
            siteName: '',
            loginMethod: 'auto_login',
          });
          const autoLoginUrl = regionedAppHostUrl(true);
          window.dispatchEvent(
            new CustomEvent('autoLoginRequired', {
              detail: { url: autoLoginUrl },
            })
          );
          return;
        }

        // Normal logged-out state (no cookie, no session)
        // Dispatch event to close any open dialog (cross-tab scenario)
        // This handles the case where user completed logout in another tab
        if (shouldCheckCookie) {
          window.dispatchEvent(new CustomEvent('authStateResolved'));
        }

        // CRITICAL: For silent polling errors, we MUST set loading to false
        if (options?.silent) {
          setLoading(false);
        }
      } finally {
        // For non-silent calls, always set loading to false
        if (!options?.silent) {
          setLoading(false);
        }
      }
    },
    []
  );

  // useEffect #1: Initial auth check on mount
  useEffect(() => {
    if (initialCheckDoneRef.current) return;
    initialCheckDoneRef.current = true;

    const initializeAuth = async () => {
      // Case 1: Auth code from OAuth callback
      if (authCode) {
        sendLoginRequest(new Map().set('one_time_token', authCode)).then(() => {
          // Clean up URL after auth code exchange
          if (typeof window !== 'undefined') {
            const url = new URL(window.location.href);
            url.searchParams.delete('code');
            window.history.replaceState({}, '', url.toString());
          }
        });
        return;
      }

      // Case 2: Check existing session via /users/me with full cookie check
      await fetchUserData({ isInitialLoad: true });
    };

    initializeAuth();
  }, [authCode, sendLoginRequest, fetchUserData]);

  // useEffect #2: 5-minute polling for token refresh (only when logged in)
  useEffect(() => {
    if (!initialCheckDoneRef.current) return;

    if (!user) return;

    const intervalId = setInterval(() => {
      fetchUserData({ silent: true }); // Silent mode - no loading flicker on success
    }, POLLING_INTERVAL);

    return () => clearInterval(intervalId);
  }, [user, fetchUserData]);

  // useEffect #3: Visibility change handler for cross-tab sync
  // Handles both AUTO_LOGIN and AUTO_LOGOUT when tab becomes visible
  useEffect(() => {
    if (!initialCheckDoneRef.current) return;

    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        // Tab became visible — check for cross-tab logout silently.
        // Using silent: true avoids setting loading=true, which would
        // flash a full-page spinner every time the user switches tabs.
        await fetchUserData({ isInitialLoad: true, silent: true });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchUserData]);

  // Logout handler
  const onLogout = async () => {
    setLoading(true); // Show loading during logout
    try {
      await beehiveGetFetcher('public/api/help-docs/logout');
    } finally {
      setUser(null);
      setSentryUser(null);
      clearSiteDetailFromStorage();
      // Page will redirect, so no need to setLoading(false)
      window.location.href = regionedAppHostUrl(false);
    }
  };

  return (
    <ClientAuthContext.Provider
      value={{
        user,
        isLoggedIn: !!user?.email,
        loading,
        refetchUserData: fetchUserData,
        onLogout,
      }}
    >
      {children}
    </ClientAuthContext.Provider>
  );
}

export default function ClientAuthProvider({
  children,
}: {
  children: ReactNode;
}) {
  const searchParams = useSearchParams();
  const code = searchParams.get('code');

  return (
    <Suspense>
      <ClientAuthProviderSuspense authCode={code}>
        {children}
      </ClientAuthProviderSuspense>
    </Suspense>
  );
}
